import { useEffect, useState } from 'react';
import { ColumnChart } from '../components/Charts.tsx';
import { api } from '../lib/api.ts';
import { formatFull, formatNumber } from '../lib/format.ts';
import { type PublicStats, useSession } from '../lib/session.ts';
import { useOnline } from '../online/client.ts';

export function StatsPage() {
  const session = useSession();
  const online = useOnline();
  const [stats, setStats] = useState<PublicStats | null>(session.stats);
  useEffect(() => {
    const load = () => api<PublicStats>('/api/stats').then(setStats).catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);
  if (!stats) return <div className="page loading"><span className="spinner big" /></div>;
  const live = online.stats ?? stats.live;
  return (
    <div className="page stats-page">
      <p className="eyebrow">{stats.day > 0 ? `Day ${stats.day} of chess, but…` : 'Chess, but…'}</p>
      <h1 className="page-title">ChessBut by the numbers</h1>
      <div className="hero-figure">
        <b>{formatFull(stats.games)}</b>
        <span>games played</span>
      </div>
      <div className="kpi-row">
        <div className="stat-tile">
          <span className="stat-label">Players</span>
          <b className="stat-value">{formatNumber(stats.players)}</b>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Countries</span>
          <b className="stat-value">{formatNumber(stats.countries)}</b>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Moves made</span>
          <b className="stat-value">{formatNumber(stats.moves)}</b>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Variants so far</span>
          <b className="stat-value">{stats.variants}</b>
        </div>
        <div className="stat-tile">
          <span className="stat-label">Peak online</span>
          <b className="stat-value">{formatNumber(Math.max(stats.peakOnline, live.online))}</b>
        </div>
        <div className="stat-tile">
          <span className="stat-label">
            <span className="live-dot" /> Online now
          </span>
          <b className="stat-value">{formatNumber(live.online)}</b>
        </div>
      </div>
      <section className="card">
        <h2 className="section-title">Games per day</h2>
        <ColumnChart data={stats.daily.map((d) => ({ label: d.day, value: d.games }))} unit="games" ariaLabel="Games played per day, last 14 days" />
      </section>
      <p className="muted small center">Today so far: {formatFull(stats.gamesToday)} games by {formatFull(stats.playersToday)} players. Updates every 30 seconds.</p>
    </div>
  );
}
